<template>
  <div class="permissions-admin">
    <v-card class="main-card pa-6 mb-6">
      <!-- Header -->
      <div class="d-flex align-center mb-6">
        <v-icon color="primary" size="32" class="mr-3">mdi-shield-account</v-icon>
        <div>
          <h2 class="text-h4 font-weight-medium primary--text">Gestione Permessi</h2>
          <p class="text-subtitle-1 text--secondary ma-0">
            Configura i permessi per ogni ruolo del sistema
          </p>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          @click="saveAllPermissions"
          :loading="saving"
          class="mr-2"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salva Modifiche
        </v-btn>
        <v-btn
          color="warning"
          text
          @click="resetToDefaults"
        >
          <v-icon left>mdi-refresh</v-icon>
          Reset Defaults
        </v-btn>
      </div>

      <!-- Permissions Table -->
      <v-data-table
        :headers="headers"
        :items="rolePermissions"
        :items-per-page="-1"
        hide-default-footer
        class="permissions-table elevation-1"
      >
        <!-- Role Name -->
        <template #item.role="{ item }">
          <div class="d-flex align-center">
            <v-avatar :color="getRoleColor(item.role)" size="24" class="mr-3">
              <v-icon dark size="16">{{ getRoleIcon(item.role) }}</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ getRoleDisplayName(item.role) }}</div>
              <div class="text-caption text--secondary">{{ item.role }}</div>
            </div>
          </div>
        </template>

        <!-- Project Access -->
        <template #item.project_access="{ item }">
          <div class="d-flex justify-center">
            <v-select
              v-model="item.permissions.project_access"
              :items="projectAccessOptions"
              dense
              small
              outlined
              hide-details
              class="permission-select"
              :menu-props="{ closeOnContentClick: true, maxHeight: 200, bottom: true, offsetY: true }"
              @change="markAsChanged(item)"
            ></v-select>
          </div>
        </template>

        <!-- Can View Expansion -->
        <template #item.can_view_expansion="{ item }">
          <div class="d-flex justify-center">
            <v-switch
              v-model="item.permissions.can_view_expansion"
              color="primary"
              hide-details
              dense
              @change="markAsChanged(item)"
            ></v-switch>
          </div>
        </template>

        <!-- Can Create Tasks -->
        <template #item.can_create_tasks="{ item }">
          <div class="d-flex justify-center">
            <v-switch
              v-model="item.permissions.can_create_tasks"
              color="primary"
              hide-details
              dense
              @change="markAsChanged(item)"
            ></v-switch>
          </div>
        </template>

        <!-- Can Edit Tasks -->
        <template #item.can_edit_tasks="{ item }">
          <div class="d-flex justify-center">
            <v-select
              v-model="item.permissions.can_edit_tasks"
              :items="editTaskOptions"
              dense
              small
              outlined
              hide-details
              class="permission-select"
              :menu-props="{ closeOnContentClick: true, maxHeight: 200, bottom: true, offsetY: true }"
              @change="markAsChanged(item)"
            ></v-select>
          </div>
        </template>

        <!-- Can Delete Tasks -->
        <template #item.can_delete_tasks="{ item }">
          <div class="d-flex justify-center">
            <v-select
              v-model="item.permissions.can_delete_tasks"
              :items="deleteTaskOptions"
              dense
              small
              outlined
              hide-details
              class="permission-select"
              :menu-props="{ closeOnContentClick: true, maxHeight: 200, bottom: true, offsetY: true }"
              @change="markAsChanged(item)"
            ></v-select>
          </div>
        </template>

        <!-- Can Export Data -->
        <template #item.can_export_data="{ item }">
          <div class="d-flex justify-center">
            <v-switch
              v-model="item.permissions.can_export_data"
              color="primary"
              hide-details
              dense
              @change="markAsChanged(item)"
            ></v-switch>
          </div>
        </template>

        <!-- Changed Indicator -->
        <template #item.changed="{ item }">
          <div class="d-flex justify-center">
            <v-chip
              v-if="item.changed"
              color="warning"
              small
              text-color="white"
            >
              <v-icon left size="14">mdi-pencil</v-icon>
              Modificato
            </v-chip>
          </div>
        </template>
      </v-data-table>

      <!-- Legend -->
      <v-card class="mt-6 pa-4" outlined>
        <div class="text-subtitle-2 font-weight-medium mb-2">
          <v-icon color="info" size="18" class="mr-1">mdi-information</v-icon>
          Legenda Permessi
        </div>
        <v-row>
          <v-col cols="12" md="4">
            <div class="text-caption mb-2">
              <strong>Accesso Progetti:</strong>
            </div>
            <ul class="text-caption text--secondary">
              <li><strong>Solo Assegnati:</strong> Vede solo progetti dove è operatore</li>
              <li><strong>Tutti:</strong> Vede tutti i progetti del sistema</li>
            </ul>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption mb-2">
              <strong>Modifica Task:</strong>
            </div>
            <ul class="text-caption text--secondary">
              <li><strong>Nessuno:</strong> Non può modificare task</li>
              <li><strong>Solo Propri:</strong> Può modificare solo i propri task</li>
              <li><strong>Tutti:</strong> Può modificare qualsiasi task</li>
            </ul>
          </v-col>
          <v-col cols="12" md="4">
            <div class="text-caption mb-2">
              <strong>Eliminazione Task:</strong>
            </div>
            <ul class="text-caption text--secondary">
              <li><strong>Nessuno:</strong> Non può eliminare task</li>
              <li><strong>Solo Propri:</strong> Può eliminare solo i propri task</li>
              <li><strong>Tutti:</strong> Può eliminare qualsiasi task</li>
            </ul>
          </v-col>
        </v-row>
      </v-card>
    </v-card>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccessMessage"
      color="success"
      timeout="3000"
      bottom
    >
      <v-icon left>mdi-check-circle</v-icon>
      Permessi salvati con successo!
    </v-snackbar>
  </div>
</template>

<script>
export default {
  name: 'PermissionsPage',
  data() {
    return {
      saving: false,
      showSuccessMessage: false,
      rolePermissions: [],

      headers: [
        {
          text: 'Ruolo',
          value: 'role',
          sortable: false,
          width: '200px'
        },
        {
          text: 'Accesso Progetti',
          value: 'project_access',
          sortable: false,
          width: '220px',
          align: 'center'
        },
        {
          text: 'Expansion Panel',
          value: 'can_view_expansion',
          sortable: false,
          width: '180px',
          align: 'center'
        },
        {
          text: 'Crea Task/Ore',
          value: 'can_create_tasks',
          sortable: false,
          width: '180px',
          align: 'center'
        },
        {
          text: 'Modifica Task',
          value: 'can_edit_tasks',
          sortable: false,
          width: '200px',
          align: 'center'
        },
        {
          text: 'Elimina Task',
          value: 'can_delete_tasks',
          sortable: false,
          width: '200px',
          align: 'center'
        },
        {
          text: 'Esporta Dati',
          value: 'can_export_data',
          sortable: false,
          width: '180px',
          align: 'center'
        },
        {
          text: 'Stato',
          value: 'changed',
          sortable: false,
          width: '100px',
          align: 'center'
        }
      ],

      projectAccessOptions: [
        { text: 'Solo Assegnati', value: 'assigned_only' },
        { text: 'Tutti', value: 'all' }
      ],

      editTaskOptions: [
        { text: 'Nessuno', value: 'none' },
        { text: 'Solo Propri', value: 'own_only' },
        { text: 'Tutti', value: 'all' }
      ],

      deleteTaskOptions: [
        { text: 'Nessuno', value: 'none' },
        { text: 'Solo Propri', value: 'own_only' },
        { text: 'Tutti', value: 'all' }
      ]
    }
  },

  mounted() {
    this.loadPermissions()
  },

  methods: {
    async loadPermissions() {
      try {
        const response = await this.$userService.getRolePermissions()
        this.rolePermissions = response.data.role_permissions.map(roleData => ({
          role: roleData.role,
          permissions: roleData.permissions,
          changed: false
        }))
      } catch (error) {
        console.error('Errore nel caricamento permessi:', error)

        // Fallback ai dati di default
        this.rolePermissions = [
          {
            role: 'GRAPHIC_OPERATOR',
            permissions: {
              project_access: 'assigned_only',
              can_view_expansion: true,
              can_create_tasks: true,
              can_edit_tasks: 'own_only',
              can_delete_tasks: 'none',
              can_export_data: false
            },
            changed: false
          },
          {
            role: 'WEB_OPERATOR',
            permissions: {
              project_access: 'assigned_only',
              can_view_expansion: true,
              can_create_tasks: true,
              can_edit_tasks: 'own_only',
              can_delete_tasks: 'none',
              can_export_data: true
            },
            changed: false
          },
          {
            role: 'PURCHASE_OPERATOR',
            permissions: {
              project_access: 'all',
              can_view_expansion: true,
              can_create_tasks: true,
              can_edit_tasks: 'all',
              can_delete_tasks: 'own_only',
              can_export_data: true
            },
            changed: false
          },
          {
            role: 'MANAGEMENT_CONTROL',
            permissions: {
              project_access: 'all',
              can_view_expansion: false,
              can_create_tasks: false,
              can_edit_tasks: 'none',
              can_delete_tasks: 'none',
              can_export_data: true
            },
            changed: false
          }
        ]
        this.$dtoast.pop({
          preset: "error",
          content: 'Errore nel caricamento dei permessi, caricati dati di default'
        })
      }
    },

    async saveAllPermissions() {
      this.saving = true
      try {
        await this.$userService.updateRolePermissions(this.rolePermissions)

        console.log('Permissions saved successfully')

        // Reset changed flags
        this.rolePermissions.forEach(role => {
          role.changed = false
        })

        this.showSuccessMessage = true
      } catch (error) {
        console.error('Errore nel salvataggio permessi:', error)
        this.$dtoast.pop({
          preset: "error",
          content: 'Errore nel salvataggio dei permessi'
        })
      } finally {
        this.saving = false
      }
    },

    resetToDefaults() {
      this.$q.dialog({
        title: 'Reset ai Valori di Default',
        message: 'Sei sicuro di voler resettare tutti i permessi ai valori di default?',
        cancel: true,
        persistent: true,
        color: 'warning'
      }).onOk(async () => {
        try {
          await this.$userService.resetRolePermissions()
          this.$dtoast.pop({
            preset: "success",
            content: 'Permessi resettati ai valori di default'
          })
          this.loadPermissions()
        } catch (error) {
          console.error('Errore nel reset:', error)
          this.$dtoast.pop({
            preset: "error",
            content: 'Errore nel reset dei permessi'
          })
        }
      })
    },

    markAsChanged(item) {
      item.changed = true
    },

    getRoleDisplayName(role) {
      const roleNames = {
        'GRAPHIC_OPERATOR': 'Operatore Grafico',
        'WEB_OPERATOR': 'Operatore Web',
        'PURCHASE_OPERATOR': 'Operatore Acquisti',
        'MANAGEMENT_CONTROL': 'Controllo di Gestione',
        'HEAD': 'Responsabile',
        'ADMIN': 'Amministratore'
      }
      return roleNames[role] || role
    },

    getRoleColor(role) {
      const colors = {
        'GRAPHIC_OPERATOR': 'deep-purple',
        'WEB_OPERATOR': 'blue',
        'PURCHASE_OPERATOR': 'green',
        'MANAGEMENT_CONTROL': 'orange',
        'HEAD': 'red',
        'ADMIN': 'black'
      }
      return colors[role] || 'grey'
    },

    getRoleIcon(role) {
      const icons = {
        'GRAPHIC_OPERATOR': 'mdi-palette',
        'WEB_OPERATOR': 'mdi-web',
        'PURCHASE_OPERATOR': 'mdi-cart',
        'MANAGEMENT_CONTROL': 'mdi-chart-line',
        'HEAD': 'mdi-account-tie',
        'ADMIN': 'mdi-shield-crown'
      }
      return icons[role] || 'mdi-account'
    }
  }
}
</script>

<style lang="scss" scoped>
.permissions-admin {
  .main-card {
    background: #fafafa;
  }

  .permissions-table {
    background: white;
    border-radius: 8px;
    margin-bottom: 200px;

    ::v-deep .v-data-table__wrapper {
      border-radius: 8px;
    }

    ::v-deep th {
      background-color: #f5f5f5;
      color: #333;
      font-weight: 600;
    }

    ::v-deep td {
      border-bottom: 1px solid #e0e0e0;
    }
  }

  .permission-select {
    width: 160px;
    max-width: 160px;

    ::v-deep .v-select__slot {
      min-height: 24px;
    }

    ::v-deep .v-input__control {
      min-height: 24px;
    }

    ::v-deep .v-input__slot {
      padding: 0 8px;
    }

    ::v-deep .v-text-field__details {
      display: none;
    }

    // Migliora il positioning delle dropdown
    ::v-deep .v-menu__content {
      z-index: 1000 !important;
      max-height: 200px;
    }

    ::v-deep .v-select__selections {
      overflow: hidden;
    }
  }
}
</style>